import React, { useState, useEffect } from "react";
import { Eye } from "lucide-react";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import TopBarFilter from "./TopBarFilter";
import BottomNav from "./BottomNav";
import MoreSheet from "./MoreSheet";
import MobileDashboardTabs from "./MobileDashboardTabs";
import { NAV_GROUPS, MOBILE_PRIMARY, viewerGroups } from "./navModel";
import IssuesRail from "../../components/issues/IssuesRail";
import IssuesSheet from "../../components/issues/IssuesSheet";
import IssuesBell from "../../components/issues/IssuesBell";
import DashboardHeaderActions from "../../components/dashboard/DashboardHeaderActions";
import IssueFormModal from "../../components/issues/IssueFormModal";
import { openCount } from "../../utils/issues";
import { installPrintFit } from "../../utils/printFit";
import { printSpecFor } from "../../utils/printPages";

function buildMobileItems() {
  const seen = new Set();
  const items = [];
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (MOBILE_PRIMARY.includes(item.tab) && !seen.has(item.tab)) {
        seen.add(item.tab);
        items.push(item);
      }
    }
  }
  return items;
}

const MOBILE_ITEMS = buildMobileItems();
const ISSUE_TABS = ["overview", "dashboard"];
// แท็บกลุ่ม Dashboard ทั้งหมด — ใช้แสดงแถบแท็บแนวนอนบนมือถือ (เห็นทุกหน้าโดยไม่ต้องเปิด More)
const DASH_GROUP = NAV_GROUPS.find((g) => g.label === "Dashboard");
const DASHBOARD_ITEMS = DASH_GROUP ? DASH_GROUP.items : [];
const DASHBOARD_TABS = DASHBOARD_ITEMS.map((i) => i.tab);

export default function Shell({
  active = {},
  onNavigate,
  title,
  liveStatus,
  projectInfo,
  onProjectChange,
  moreOpen,
  setMoreOpen,
  issues = [],
  onSaveIssue,
  onSetIssueStatus,
  onDeleteIssue,
  segmentRecords = [],
  groutRecords = [],
  shiftReports = [],
  activeMachine,
  onMachineChange,
  currentRingNum,
  globalFilter,
  isViewer = false,
  // The sync status button, and the panels behind it. Passed in rather than imported: this layer is
  // the design system, and the offline stack is the app.
  statusButton = null,
  offlineOverlays = null,
  // "ติดตั้งแอป" belongs in both places the plan names: the status centre, and here — MoreSheet is
  // the discovery surface on a phone, which is the device this is for.
  moreSheetFooter = null,
  children,
}) {
  const showIssues = ISSUE_TABS.includes(active.tab); // viewer ก็เห็น (read-only) — ส่ง readOnly ไป rail
  const showFilter =
    active.tab === "dashboard" ||
    active.tab === "performance" ||
    (active.tab === "analysis" && (active.module === "segment" || active.module === "route"));
  const [sheetOpen, setSheetOpen] = useState(false);
  const [modal, setModal] = useState({ open: false, editing: null });

  const openAdd = () => { setSheetOpen(false); setModal({ open: true, editing: null }); };
  const openEdit = (issue) => { setSheetOpen(false); setModal({ open: true, editing: issue }); };
  const submitIssue = (form) => { onSaveIssue(form); setModal({ open: false, editing: null }); };
  const closeIssue = (id) => onSetIssueStatus(id, "closed");
  const reopenIssue = (id) => onSetIssueStatus(id, "open");

  const mobileItems = isViewer ? viewerGroups().flatMap((g) => g.items) : MOBILE_ITEMS;

  // ปริ้นได้ทุกหน้า: Ctrl+P / File→Print ปลุก beforeprint -> handler ย่อกล่องของหน้าที่เปิดอยู่ให้พอดี A4
  // ผูกกับ tab/module เพราะแต่ละหน้าใช้แนวกระดาษคนละแบบ (ดู utils/printPages.js)
  useEffect(
    () => installPrintFit(() => ({
      el: document.getElementById("page-print-root"),
      ...printSpecFor(active.tab, active.module),
    })),
    [active.tab, active.module]
  );

  const railProps = {
    issues,
    onAdd: openAdd,
    onEdit: openEdit,
    onCloseIssue: closeIssue,
    onReopenIssue: reopenIssue,
    onDeleteIssue,
    currentRingNum,
    readOnly: isViewer,
  };

  return (
    <div className="flex min-h-screen bg-surface-page font-sans">
      <Sidebar active={active} onNavigate={onNavigate} liveStatus={liveStatus} machine={activeMachine} isViewer={isViewer} />

      <div className="flex-1 flex flex-col min-w-0">
        <TopBar
          title={title}
          liveStatus={liveStatus}
          projectInfo={projectInfo}
          onProjectChange={onProjectChange}
          compact={false}
          machine={activeMachine}
          onMachineChange={onMachineChange}
          rightSlot={
            <div className="flex items-center gap-2">
              {/* On every tab, because a stuck write is not a fact about one page — but not in the
                  viewer link: that browser has its own queue, and a crew member opening the owner's
                  link on their working phone would get live retry and discard over their own
                  unsent rings. */}
              {!isViewer && statusButton}
              {isViewer && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-navy bg-cyan-tint border border-line px-2 py-1 rounded-badge whitespace-nowrap">
                  <Eye size={13} /> Viewer
                </span>
              )}
              {showFilter && globalFilter && <TopBarFilter state={globalFilter.state} setters={globalFilter.setters} />}
              {active.tab === "dashboard" && <DashboardHeaderActions segmentRecords={segmentRecords} groutRecords={groutRecords} shiftReports={shiftReports} isViewer={isViewer} />}
              {showIssues && <IssuesBell count={openCount(issues)} onClick={() => setSheetOpen(true)} />}
            </div>
          }
        />

        <main className="flex-1 pb-[calc(64px+env(safe-area-inset-bottom))] lg:pb-0">
          {DASHBOARD_TABS.includes(active.tab) && (
            <MobileDashboardTabs items={DASHBOARD_ITEMS} activeTab={active.tab} activeModule={active.module} onNavigate={onNavigate} />
          )}
          {showIssues ? (
            <div className="flex w-full print:block">
              <div className="flex-1 min-w-0 px-4 sm:px-6 py-6 print:p-0 print:m-0">
                <div id="page-print-root" className="max-w-[1200px] mx-auto w-full print:max-w-none">{children}</div>
              </div>
              <IssuesRail {...railProps} />
            </div>
          ) : (
            <div id="page-print-root" className="px-4 sm:px-6 py-6 w-full print:p-0 print:m-0">{children}</div>
          )}
        </main>
      </div>

      <BottomNav items={mobileItems} activeTab={active.tab} activeModule={active.module} onNavigate={onNavigate} onMore={isViewer ? undefined : () => setMoreOpen(true)} />
      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} onNavigate={onNavigate} footer={moreSheetFooter} />

      {/* At the root, above BottomNav (z-40) and MoreSheet (z-50), so a phone and a desktop show the
          same panels from the same state rather than one copy per layout. */}
      {offlineOverlays}

      {showIssues && (
        <>
          <IssuesSheet open={sheetOpen} onDismiss={() => setSheetOpen(false)} {...railProps} />
          <IssueFormModal open={modal.open} initial={modal.editing} onSubmit={submitIssue} onClose={() => setModal({ open: false, editing: null })} currentRingNum={currentRingNum} defaultMachine={activeMachine} />
        </>
      )}
    </div>
  );
}
